import React, {
  FC,
  useRef,
  useState,
  useEffect,
  ReactNode,
  useContext,
  createContext,
  HTMLAttributes,
  DetailedHTMLProps,
} from 'react'

import ReactDOM from 'react-dom'

const Context = createContext<HTMLDivElement>(null)

type DivProps = DetailedHTMLProps<
  HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>

interface ProviderProps {
  children: ReactNode
}

interface ModalProps extends DivProps {
  children: ReactNode
  onClose?: () => void
  clickOutsideEnabled?: boolean
}

export const ModalProvider: FC<ProviderProps> = ({ children }) => {
  const modalRef = useRef<HTMLDivElement>()
  const [context, setContext] = useState<HTMLDivElement>()

  // make sure re-render is triggered after initial
  // render so that modalRef exists
  useEffect(() => {
    setContext(modalRef.current)
  }, [])

  return (
    <div className="modal__provider">
      <Context.Provider value={context}>{children}</Context.Provider>
      <div ref={modalRef} />
    </div>
  )
}

export const Modal: FC<ModalProps> = ({ children, ...props }) => {
  const modalNode = useContext(Context)

  return modalNode
    ? ReactDOM.createPortal(
        <ModalBody {...props}>{children}</ModalBody>,
        modalNode
      )
    : null
}

const ModalBody: FC<ModalProps> = ({
  children,
  className,
  onClose = () => {},
  clickOutsideEnabled = true,
  ...props
}) => {
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!clickOutsideEnabled) return
    function closeModal(e) {
      if (contentRef?.current?.contains(e?.target)) return
      onClose()
    }

    document.addEventListener('mousedown', closeModal)

    return () => {
      document.removeEventListener('mousedown', closeModal)
    }
  }, [onClose, clickOutsideEnabled])

  return (
    <div className="modal__overlay">
      <div className="modal__container">
        <div
          ref={contentRef}
          className={`modal__content ${className ? className : ''}`}
          {...props}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
